import { useCallback, useState } from 'react';
import { SymbolSearch } from '../components/SymbolSearch';
import { HideToggle } from '../components/HideToggle';
import * as api from '../api/client';

import { money, moneyExact, pct, shortDate, todayIso , isDay} from '../lib/format';
import { bandPct } from '../lib/capBands';
import { newId } from '../lib/id';
import { decimalOr, isPositiveDecimal } from '../lib/number';
import { toUsd } from '../state/derive';
import { useStore } from '../state/store';
import { effectOf, moveCash, settle } from '../state/cashMove';
import { buyCost, costWithFee } from '../state/commission';
import { doubtAboutPrice } from '../state/priceSanity';
import { addToLot, lotFor } from '../state/addToLot';
import type { Account, CapKind, Currency, Holding, Quote, Thesis, Trade } from '../types';
import { coinOf, coinPerpSymbol, kindOf } from '../state/assetKind';
import type { Nav } from '../App';

export function AddPosition({ nav }: { nav: Nav }) {
  const { book, state, limits, putHolding, putThesis, setCap, putTrade, setCash } = useStore();
  const accounts = state.data.accounts;
  const currency = state.settings.displayCurrency;
  const fx = state.fx;

  const [symbol, setSymbol] = useState('');
  const [resolved, setResolved] = useState<Quote | null>(null);
  /* A coin MEXC lists and Yahoo does not. Yahoo answers for a few hundred
     coins; MEXC's board carries over a thousand, and the ones it alone carries
     are exactly the ones somebody has to go looking for. */
  const [coinMark, setCoinMark] = useState<{ symbol: string; price: number } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const [account, setAccount] = useState<Account>(accounts[0]?.id ?? 'revolut');
  const [shares, setShares] = useState('');
  const [avgCost, setAvgCost] = useState('');
  const [fee, setFee] = useState('');
  /* When it happened, not when it is being typed in. Every dated reading takes
     this: the markers on the price chart, the earliest-parcel-first matching on
     the tax screen, and the share count a dividend was paid on. Today is the
     default because most records are written the day of. */
  const [at, setAt] = useState(todayIso());
  const [cap, setCapKind] = useState<CapKind | ''>('');

  const [why, setWhy] = useState('');
  const [abortMetric, setAbortMetric] = useState('');
  const [checkBy, setCheckBy] = useState('');
  const [lossIfWrong, setLossIfWrong] = useState('');
  const [saving, setSaving] = useState(false);

  /* The Yahoo symbol is confirmed before anything is stored, and the exact
     string that answered is what gets written to the record — never guessed at
     call time later (§3.2). */
  const resolve = useCallback(async (exact?: string) => {
    const s = (exact ?? symbol).trim().toUpperCase();
    if (!s) return;
    setResolving(true);
    setResolveError(null);
    setResolved(null);
    setCoinMark(null);
    try {
      const { rows, failed } = await api.fetchQuotes([s]);
      const q = rows[0];
      if (q) {
        setResolved(q);
        return;
      }
      /* Nothing from Yahoo. Before reporting that, ask MEXC — the board is
         already fetched for the contracts and it prices coins Yahoo has never
         heard of. The index is asked for, not the contract's own traded price:
         that is MEXC's answer to what the coin is worth, which is the question,
         and it is the same reading this book already trusts for a company whose
         exchange is shut. */
      const contract = coinPerpSymbol(coinOf(s));
      const board = await api.fetchPerpMarks([contract]);
      const mark = board.rows[0];
      if (mark && mark.indexPrice > 0) {
        setCoinMark({ symbol: contract, price: mark.indexPrice });
        return;
      }
      setResolveError(
        failed.length
          ? `Neither Yahoo nor MEXC knows ${s}. Non-US listings need their suffix: 2498.HK, `
            + 'EL.PA, PZU.WA. Coins are BTC-USD on Yahoo and BTC on MEXC.'
          : 'No answer came back.',
      );
    } catch {
      setResolveError('Could not reach the price service. You can still save and confirm later.');
    } finally {
      setResolving(false);
    }
  }, [symbol]);

  const shareCount = decimalOr(shares);
  const cost = decimalOr(avgCost);
  const feePaid = decimalOr(fee);
  /* What the book keeps as the average: the price plus the commission spread
     across the shares it bought. Money spent to acquire something is part of
     what it cost, and it is what a tax return asks for. */
  const basis = costWithFee(shareCount, cost, feePaid);
  /* The app knows the mark while it is being told a price. A figure well away
     from it is worth saying out loud — see `state/priceSanity.ts`. */
  // The mark, from whichever of the two answered. A coin's index is in USDT,
  // which this book carries as dollars.
  const mark = resolved ? resolved.price : coinMark?.price ?? null;
  const priceDoubt = mark !== null ? doubtAboutPrice(cost, mark, shareCount) : null;
  const listingCcy = (resolved?.currency ?? 'USD') as Currency;
  const markUsd = mark !== null ? toUsd(mark, listingCcy, fx) ?? 0 : 0;
  const valueUsd = shareCount * markUsd;

  // Adding to the book grows the denominator as well as the position, so the
  // resulting weight is measured against the larger total.
  const existing = book.positions.find((p) => p.ticker === (resolved?.symbol ?? symbol.trim().toUpperCase()));
  const newTotal = book.totals.totalUsd + valueUsd;
  const resultingWeight = newTotal > 0
    ? (((existing?.marketValue ?? 0) + valueUsd) / newTotal) * 100
    : 0;
  const bands = state.data.capBands;
  const capValue = bandPct(bands, cap === '' ? null : cap);
  const risk = resultingWeight * (decimalOr(lossIfWrong) / 100);

  const thesisComplete =
    why.trim() !== '' && abortMetric.trim() !== '' && checkBy !== '' && isPositiveDecimal(lossIfWrong);
  const canSave = shareCount > 0 && (resolved !== null || symbol.trim() !== '') && isDay(at);

  const commit = async (withThesis: boolean) => {
    if (!canSave || saving) return;
    setSaving(true);
    const typed = symbol.trim().toUpperCase();
    /* Coin or company, and for a coin the two symbols come apart.
       *
       * Yahoo calls Bitcoin `BTC-USD`; nobody else does. `ticker` is the name on
       * screen and the key eight kinds of record join on, `yahooSymbol` is the
       * exact string that answered — which is why they are separate fields, and
       * this is the case they were separate for. `BTC-USD` as the display
       * ticker would put the quote currency into every heading and every
       * theme. */
    const kind = kindOf(undefined, resolved ?? undefined);
    const isCoin = kind === 'crypto' || coinMark !== null;
    const ticker = isCoin
      ? coinOf(resolved?.symbol ?? typed)
      : (resolved?.symbol ?? typed);
    /* An account that already holds this gets the purchase averaged into the
       lot it already has. `Holding` is one ticker inside one account, and a
       second row for the same pair splits the cost basis across two records
       that each know half of it — which is how a corrected trade stops being
       able to speak for the position at all. */
    const already = lotFor(state.data.holdings, ticker, account);
    const merged = already
      ? addToLot(already, { shares: shareCount, price: cost, fee: feePaid })
      : null;

    if (merged) {
      await putHolding(merged.next);
    } else {
      const holding: Holding = {
        id: newId('hold'),
        ticker,
        yahooSymbol: resolved?.symbol ?? ticker,
        account,
        shares: shareCount,
        avgCost: basis,
        currency: listingCcy,
        openedAt: at,
      };
      if (resolved?.shortName) holding.name = resolved.shortName;
      /* Recorded rather than worked out later, for the same reason the fetch
         symbol is: a coin MEXC alone lists has no quote to read it off, and a
         cold start with no network should not change what something is. */
      if (isCoin) holding.assetKind = 'crypto';
      await putHolding(holding);
    }

    const trade: Trade = {
      id: newId('trade'),
      ticker,
      account,
      side: 'buy',
      shares: shareCount,
      price: cost,
      // Noon, so a date input's day is unambiguous wherever it is read back.
      at: `${at}T12:00:00.000Z`,
    };
    if (feePaid > 0) trade.fee = feePaid;
    /* Buying takes the money out of the account it was bought from. The mirror
       of the sale screen, which has always put the proceeds back in — a book
       that credits one and not the other reports spare cash it does not have.
       What it moved is remembered on the trade, so correcting or deleting the
       trade can take it back exactly. Bulk seeding has its own path. */
    const spend = effectOf(moveCash(
      book.cashByAccount, state.data.accounts, account,
      -buyCost(shareCount, cost, feePaid), listingCcy, state.fx,
    ));
    if (spend) trade.cashEffect = spend;

    await putTrade(trade);
    for (const m of settle(book.cashByAccount, [spend])) {
      await setCash(m.account, m.amount, m.currency);
    }

    if (withThesis && thesisComplete) {
      const thesis: Thesis = {
        ticker,
        why: why.trim(),
        abortMetric: abortMetric.trim(),
        checkBy,
        lossIfWrong: decimalOr(lossIfWrong),
        revisions: [],
      };
      await putThesis(thesis);
    }
    if (cap !== '') await setCap(ticker, cap);

    setSaving(false);
    nav.go({ name: 'position', ticker });
  };

  return (
    <div className="screen">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>Add a position</h1>
        <HideToggle push />
      </div>

      <div className="card card-pad">
        {/* Searched by name as well as by ticker, because not everything you can
            hold is a company you can name the ticker of: gold answers to GC=F,
            and the only person who knows that is someone who already knew. */}
        <SymbolSearch
          label="Ticker or name"
          placeholder="ZETA, gold, 2498.HK"
          onPick={(hit) => { setSymbol(hit.symbol); void resolve(hit.symbol); }}
        />

        <div className="field">
          <label htmlFor="ticker">Or type the exact symbol</label>
          <div className="control-row" style={{ marginBottom: 0 }}>
            <input
              id="ticker" className="input" value={symbol} autoCapitalize="characters"
              autoCorrect="off" spellCheck={false} placeholder="ZETA"
              onChange={(e) => { setSymbol(e.target.value); setResolved(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') void resolve(); }}
            />
            <button className="btn" onClick={() => void resolve()} disabled={resolving || !symbol.trim()}>
              {resolving ? 'Checking…' : 'Check'}
            </button>
          </div>

          {resolved ? (
            <div className="note" style={{ marginTop: 8 }}>
              <strong>{resolved.shortName ?? resolved.symbol}</strong> on {resolved.exchangeName || 'an unnamed exchange'},
              quoted in {resolved.currency} at {moneyExact(resolved.price, resolved.currency as 'USD')}.
              Saving stores <span className="num">{resolved.symbol}</span> as the fetch symbol.
              {resolved.exchangeName === 'CCC'
                ? ' Held as a coin: it trades around the clock, so it is left out of'
                  + ' whether the book calls the market open, and the sections about earnings and'
                  + ' dividends do not apply to it.'
                : ''}
            </div>
          ) : null}
          {coinMark ? (
            <div className="note" style={{ marginTop: 8 }}>
              Yahoo does not list this one. <strong>MEXC</strong> does, at{' '}
              {moneyExact(coinMark.price, 'USD')}, its index for{' '}
              <span className="num">{coinMark.symbol}</span>, which is the exchange's reading of what
              the coin is worth rather than what the contract itself changes hands at. That is the
              price this position will carry, and the screen says so wherever it is used. There is
              no daily history behind it, so the price chart stays empty.
            </div>
          ) : null}
          {resolveError ? (
            <div className="note" data-tone="warn" style={{ marginTop: 8 }}>{resolveError}</div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="account">Account</label>
          <select id="account" className="select" value={account}
                  onChange={(e) => setAccount(e.target.value as Account)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div className="field" style={{ flex: 1 }}>
            {/* Follows what was resolved: "Shares" over a box that is about to
                take 0.05 of a Bitcoin is the kind of wrong that makes somebody
                check whether the app understood them. */}
            <label htmlFor="shares">
              {resolved?.exchangeName === 'CCC' || coinMark ? 'Units' : 'Shares'}
            </label>
            <input id="shares" className="input" data-mono="true" inputMode="decimal"
                   value={shares} onChange={(e) => setShares(e.target.value)} placeholder="0" />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="cost">Average cost</label>
            <input id="cost" className="input" data-mono="true" inputMode="decimal"
                   value={avgCost} onChange={(e) => setAvgCost(e.target.value)} placeholder="0.00" />
          </div>
        </div>

        {priceDoubt ? (
          <div className="note" data-tone="warn" style={{ marginTop: 4, marginBottom: 12 }}>
            {cost.toFixed(2)} is {priceDoubt.multiple.toFixed(1)}× the market price of{' '}
            {resolved!.price.toFixed(2)}.
            {priceDoubt.totalLooksRight ? (
              <>
                {' '}If that was the total for {shareCount} shares, the price per share is{' '}
                <strong>{priceDoubt.ifTotal.toFixed(2)}</strong>, which is about where the market
                is. This field is per share.
              </>
            ) : (
              <> This field is per share. Fine if that is really what you paid.</>
            )}
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="a-at">When you bought</label>
          <input id="a-at" className="input" type="date" max={todayIso()}
                 value={at} onChange={(e) => setAt(e.target.value)} />
          <div className="hint">
            {at === todayIso()
              ? 'Today unless you say otherwise. It dates the purchase and, for a position the '
                + 'book has not held before, the day it opened.'
              : `Recorded on ${shortDate(at)}, not today.`}
          </div>
        </div>

        <div className="field">
          <label htmlFor="fee">Commission {resolved ? `(${listingCcy})` : ''}</label>
          <input id="fee" className="input" data-mono="true" inputMode="decimal"
                 value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0.00" />
          <div className="hint">
            What the broker charged to open it. Leave it empty if there was none.
            {feePaid > 0 && shareCount > 0 && cost > 0 ? (
              <>
                {' '}The average kept will be{' '}
                <span className="mono">{basis.toFixed(4)}</span> rather than{' '}
                <span className="mono">{cost.toFixed(4)}</span>, the fee spread across the shares,
                which is what it cost you to own them and what a tax return asks for. Your broker
                will still show the price without it.
              </>
            ) : null}
          </div>
        </div>

        <div className="field">
          <label htmlFor="capsel">Size ceiling</label>
          <select id="capsel" className="select" value={cap}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCapKind(v !== '' && bands.some((b) => b.id === v) ? v : '');
                  }}>
            <option value="">
              {bands.length === 0 ? 'No ceilings defined' : 'No ceiling set · it will be flagged'}
            </option>
            {bands.map((b) => (
              <option key={b.id} value={b.id}>{b.name} · {b.pct}%</option>
            ))}
          </select>
        </div>
      </div>

      {/* Live readout of what this does to the book (§7.3) */}
      <div className="card card-pad" style={{ marginTop: 10 }}>
        <div className="kv-list">
          <div className="kv">
            <span className="k">This buys</span>
            <span className="v num">
              {resolved ? money(currency === 'PLN' && fx?.usdPln ? valueUsd * fx.usdPln : valueUsd, currency) : '—'}
            </span>
          </div>
          {feePaid > 0 && shareCount > 0 && cost > 0 ? (
            <div className="kv">
              <span className="k">Leaves the account</span>
              <span className="v num">
                {money(buyCost(shareCount, cost, feePaid), listingCcy as 'USD')}
                <span className="dim"> incl. {money(feePaid, listingCcy as 'USD')} commission</span>
              </span>
            </div>
          ) : null}
          <div className="kv">
            <span className="k">Resulting weight</span>
            <span className={`v num ${capValue !== null && resultingWeight > capValue ? 'crit' : ''}`}>
              {resolved ? pct(resultingWeight) : '—'}
            </span>
          </div>
          <div className="kv">
            <span className="k">Ceiling that applies</span>
            <span className="v num">{capValue !== null ? pct(capValue) : 'none set'}</span>
          </div>
          <div className="kv">
            <span className="k">Risk to the whole book</span>
            <span className={`v num ${risk > limits.riskPerPosition ? 'crit' : ''}`}>
              {isPositiveDecimal(lossIfWrong) ? pct(risk) : '—'}
            </span>
          </div>
        </div>
        {capValue !== null && resultingWeight > capValue ? (
          <div className="note" data-tone="crit" style={{ marginTop: 12 }}>
            That puts it over its ceiling the moment you save it.
          </div>
        ) : null}
        {existing ? (
          <div className="note" style={{ marginTop: 12 }}>
            You already hold {existing.ticker} in {existing.accounts.length === 1 ? 'one account' : `${existing.accounts.length} accounts`}.
            {lotFor(state.data.holdings, resolved?.symbol ?? symbol.trim().toUpperCase(), account)
              ? ' This averages into what is already in that account rather than opening a second lot beside it.'
              : ' This opens a lot in that account; the others are untouched.'}
          </div>
        ) : null}
      </div>

      <section>
        <div className="section-head"><h2>Before you save</h2></div>
        <div className="card card-pad">
          <div className="field field-required">
            <label htmlFor="a-why">Why I own it</label>
            <textarea id="a-why" className="textarea" value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="The mechanism. What has to happen in the business for this to work." />
          </div>
          <div className="field field-required">
            <label htmlFor="a-abort">What proves me wrong</label>
            <textarea id="a-abort" className="textarea" value={abortMetric}
                      onChange={(e) => setAbortMetric(e.target.value)}
                      placeholder="In business terms. What number would end this?" />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="field field-required" style={{ flex: 1 }}>
              <label htmlFor="a-check">Check by</label>
              <input id="a-check" className="input" type="date" value={checkBy}
                     onChange={(e) => setCheckBy(e.target.value)} />
            </div>
            <div className="field field-required" style={{ flex: 1 }}>
              <label htmlFor="a-loss">Falls by (%)</label>
              <input id="a-loss" className="input" data-mono="true" inputMode="decimal"
                     value={lossIfWrong} onChange={(e) => setLossIfWrong(e.target.value)} />
            </div>
          </div>
        </div>
      </section>

      <button className="btn btn-primary btn-block" style={{ marginTop: 16 }}
              disabled={!canSave || !thesisComplete || saving}
              onClick={() => void commit(true)}>
        Save position
      </button>

      {/* Kept on purpose (§7.3): a form that refuses to save teaches you to type
          filler into the thesis field, which is worse than an honest flag. */}
      <button className="btn btn-block" style={{ marginTop: 9 }}
              disabled={!canSave || saving}
              onClick={() => void commit(false)}>
        Save without a thesis · flag it as undocumented
      </button>

      <p className="dim" style={{ fontSize: 11, textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
        This only writes it down. Place the order at your broker first.
      </p>
    </div>
  );
}
